/**
 * Thin promise wrapper around IndexedDB.
 *
 * Spec ref §15.7. Schema for the local-first store:
 *   designs       — { id, name, sceneJson, createdAt, updatedAt, syncState }
 *   ops           — append-only steps history (autoIncrement key)
 *   pending_sync  — outbound queue for post-verification sync (autoIncrement)
 *
 * No external dep — keeps the bundle lean (this is ~120 lines vs. idb's
 * ~1.5 kB; the extra polish isn't worth it for the surface we use).
 *
 * Schema versions:
 *   v1 — initial three stores
 *   v2 — `name` field added to design records (existing rows get filled
 *        with a default name in onupgradeneeded)
 */

const DB_NAME = 'maysterko';
const DB_VERSION = 2;

export type SyncState = 'local-only' | 'pending' | 'synced';

export interface DesignRecord {
  id: string;
  /** User-facing label, e.g. "Spaceship", "Dragon house". */
  name: string;
  sceneJson: string;
  createdAt: number;
  updatedAt: number;
  syncState: SyncState;
}

let dbPromise: Promise<IDBDatabase> | null = null;

function ensureStores(db: IDBDatabase): void {
  if (!db.objectStoreNames.contains('designs')) {
    db.createObjectStore('designs', { keyPath: 'id' });
  }
  if (!db.objectStoreNames.contains('ops')) {
    db.createObjectStore('ops', { keyPath: 'seq', autoIncrement: true });
  }
  if (!db.objectStoreNames.contains('pending_sync')) {
    db.createObjectStore('pending_sync', { keyPath: 'seq', autoIncrement: true });
  }
}

function backfillNamesV2(req: IDBOpenDBRequest): void {
  const tx = req.transaction;
  if (!tx) return;
  const cursorReq = tx.objectStore('designs').openCursor();
  cursorReq.onsuccess = () => {
    const cursor = cursorReq.result;
    if (!cursor) return;
    const rec = cursor.value as Partial<DesignRecord>;
    if (rec.name == null) {
      rec.name = rec.id === 'default' ? 'My design' : nameFromArchiveId(rec.id ?? 'unknown');
      cursor.update(rec);
    }
    cursor.continue();
  };
}

function openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (event) => {
      ensureStores(req.result);
      if (event.oldVersion < 2) backfillNamesV2(req);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

/** Default human label for an archive-{ts} record from v1 backfill. */
function nameFromArchiveId(id: string): string {
  const m = /^archive-(\d+)$/.exec(id);
  if (!m) return id;
  const ts = Number(m[1]);
  if (!Number.isFinite(ts)) return id;
  const d = new Date(ts);
  return `Saved ${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
}

/** Close + drop the cached connection — only used by tests. */
export async function _resetDbForTests(): Promise<void> {
  if (dbPromise) {
    try {
      const db = await dbPromise;
      db.close();
    } catch {
      // ignore — opening may have failed; we just want the cache cleared
    }
  }
  dbPromise = null;
}

export async function saveDesign(record: DesignRecord): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('designs', 'readwrite');
    tx.objectStore('designs').put(record);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function loadDesign(id: string): Promise<DesignRecord | null> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('designs', 'readonly');
    const req = tx.objectStore('designs').get(id);
    req.onsuccess = () => resolve((req.result as DesignRecord | undefined) ?? null);
    req.onerror = () => reject(req.error);
  });
}

/** All designs ordered by updatedAt desc (most recent first). */
export async function listDesigns(): Promise<DesignRecord[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('designs', 'readonly');
    const req = tx.objectStore('designs').getAll();
    req.onsuccess = () => {
      const all = (req.result as DesignRecord[]) ?? [];
      all.sort((a, b) => b.updatedAt - a.updatedAt);
      resolve(all);
    };
    req.onerror = () => reject(req.error);
  });
}

export async function deleteDesign(id: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('designs', 'readwrite');
    tx.objectStore('designs').delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function renameDesign(id: string, name: string): Promise<void> {
  const existing = await loadDesign(id);
  if (!existing) throw new Error(`renameDesign: no design with id=${id}`);
  await saveDesign({ ...existing, name, updatedAt: Date.now() });
}
